/* KG custom <kg-select> element.
   Progressive enhancement over a native <select>: the native element stays
   in the form (so submission, variant-selects and analytics keep working),
   we only add a styled trigger + listbox on top and proxy clicks/keys to
   the underlying select. */
(function () {
  if (window.customElements && window.customElements.get('kg-select')) return;

  class KgSelect extends HTMLElement {
    connectedCallback() {
      this.native = this.querySelector('select.kg-select__native');
      this.trigger = this.querySelector('.kg-select__trigger');
      this.list = this.querySelector('.kg-select__list');
      this.valueLabel = this.querySelector('.kg-select__value');
      this.options = Array.from(this.querySelectorAll('.kg-select__option'));
      if (!this.native || !this.trigger || !this.list) return;

      this._onTriggerClick = this._onTriggerClick.bind(this);
      this._onOptionClick = this._onOptionClick.bind(this);
      this._onDocClick = this._onDocClick.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onNativeChange = this._onNativeChange.bind(this);

      this.trigger.addEventListener('click', this._onTriggerClick);
      this.list.addEventListener('click', this._onOptionClick);
      this.addEventListener('keydown', this._onKeydown);
      this.native.addEventListener('change', this._onNativeChange);

      this._activeIndex = this.options.findIndex((o) => o.getAttribute('aria-selected') === 'true');
      if (this._activeIndex < 0) this._activeIndex = 0;
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocClick);
    }

    get isOpen() {
      return this.getAttribute('data-open') === 'true';
    }

    open() {
      if (this.isOpen) return;
      this.setAttribute('data-open', 'true');
      this.trigger.setAttribute('aria-expanded', 'true');
      this.list.hidden = false;
      document.addEventListener('click', this._onDocClick);
      this._highlight(this._activeIndex);
    }

    close() {
      if (!this.isOpen) return;
      this.removeAttribute('data-open');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.list.hidden = true;
      document.removeEventListener('click', this._onDocClick);
    }

    toggle() {
      this.isOpen ? this.close() : this.open();
    }

    _onTriggerClick(e) {
      e.preventDefault();
      this.toggle();
    }

    _onOptionClick(e) {
      const opt = e.target.closest('.kg-select__option');
      if (!opt) return;
      if (opt.classList.contains('is-unavailable')) {
        // Still let the user pick an unavailable variant — that's how Dawn
        // surfaces the "sold out" state on the product form.
      }
      const value = opt.dataset.value;
      this._selectValue(value);
      this.close();
      this.trigger.focus();
    }

    _onDocClick(e) {
      if (this.contains(e.target)) return;
      this.close();
    }

    _onKeydown(e) {
      const k = e.key;
      if (!this.isOpen) {
        if (k === 'Enter' || k === ' ' || k === 'ArrowDown' || k === 'ArrowUp') {
          e.preventDefault();
          this.open();
        }
        return;
      }
      if (k === 'Escape') {
        e.preventDefault();
        this.close();
        this.trigger.focus();
      } else if (k === 'ArrowDown') {
        e.preventDefault();
        this._highlight(Math.min(this._activeIndex + 1, this.options.length - 1));
      } else if (k === 'ArrowUp') {
        e.preventDefault();
        this._highlight(Math.max(this._activeIndex - 1, 0));
      } else if (k === 'Home') {
        e.preventDefault();
        this._highlight(0);
      } else if (k === 'End') {
        e.preventDefault();
        this._highlight(this.options.length - 1);
      } else if (k === 'Enter' || k === ' ') {
        e.preventDefault();
        const opt = this.options[this._activeIndex];
        if (opt) this._selectValue(opt.dataset.value);
        this.close();
        this.trigger.focus();
      }
    }

    _highlight(idx) {
      this._activeIndex = idx;
      this.options.forEach((o, i) => {
        if (i === idx) {
          o.setAttribute('data-active', 'true');
          o.scrollIntoView({ block: 'nearest' });
        } else {
          o.removeAttribute('data-active');
        }
      });
    }

    _selectValue(value) {
      if (this.native.value === value) return;
      this.native.value = value;
      this.native.dispatchEvent(new Event('change', { bubbles: true }));
      // Visual sync happens via _onNativeChange so external code (variant
      // swapping, URL updates) that mutates the select stays consistent.
    }

    _onNativeChange() {
      const value = this.native.value;
      this.options.forEach((o, i) => {
        const selected = o.dataset.value === value;
        o.setAttribute('aria-selected', selected ? 'true' : 'false');
        if (selected) {
          this._activeIndex = i;
          if (this.valueLabel) this.valueLabel.textContent = o.dataset.label || o.textContent.trim();
        }
      });
    }
  }

  window.customElements.define('kg-select', KgSelect);
})();
